// SVG caching utility to prevent duplicate requests
// Tracks in-flight requests and caches results
// Note: SVG processing (color changes, etc.) happens AFTER caching in components

// Cache for raw SVG content (before processing)
const svgCache = {};

// Track in-flight requests to prevent duplicates
const inFlightRequests = {};

// Storage key for session SVG invalidation
const SVG_INVALIDATED_KEY = 'laptura_svg_invalidated';
const SVG_INVALIDATED_TIMESTAMP_KEY = 'laptura_svg_invalidated_ts';

/**
 * Get the shared cache-bust timestamp for SVGs (ensures all components use same URL)
 */
const getSvgInvalidationTimestamp = () => {
  let ts = sessionStorage.getItem(SVG_INVALIDATED_TIMESTAMP_KEY);
  if (!ts) {
    ts = String(Date.now());
    sessionStorage.setItem(SVG_INVALIDATED_TIMESTAMP_KEY, ts);
  }
  return ts;
};

/**
 * Check if SVGs for a session should be invalidated
 */
export const isSvgInvalidated = (sessionId) => {
  const invalidatedSession = sessionStorage.getItem(SVG_INVALIDATED_KEY);
  return invalidatedSession === String(sessionId);
};

/**
 * Mark SVGs for a session as needing refresh (call before page reload)
 */
export const invalidateSvgCache = (sessionId) => {
  sessionStorage.setItem(SVG_INVALIDATED_KEY, String(sessionId));
  // Set a shared timestamp that all SVG fetches will use for cache-busting
  sessionStorage.setItem(SVG_INVALIDATED_TIMESTAMP_KEY, String(Date.now()));
};

/**
 * Clear the SVG invalidation flag
 */
export const clearSvgInvalidation = () => {
  const invalidatedSession = sessionStorage.getItem(SVG_INVALIDATED_KEY);
  if (invalidatedSession) {
    sessionStorage.removeItem(SVG_INVALIDATED_KEY);
    sessionStorage.removeItem(SVG_INVALIDATED_TIMESTAMP_KEY);
  }
};

/**
 * Clear all SVG cache entries for a specific session
 */
export const clearSessionSvgCache = (sessionId) => {
  const keysToDelete = Object.keys(svgCache).filter(key => 
    key.includes(`session/${sessionId}`) || key.includes(`/${sessionId}/`)
  );
  keysToDelete.forEach(key => {
    delete svgCache[key];
  });
};

/**
 * Fetch and cache SVG content with deduplication
 * @param {string} url - SVG URL
 * @param {object} options - Fetch options (headers, etc.)
 * @param {boolean} forceRefresh - Force bypass cache
 * @returns {Promise<string>} SVG content as string
 */
export const fetchSvgCached = async (url, options = {}, forceRefresh = false) => {
  // Extract session ID from URL to check if it was invalidated
  const sessionMatch = url.match(/session\/(\d+)/);
  const sessionId = sessionMatch ? sessionMatch[1] : null;
  const wasInvalidated = sessionId && isSvgInvalidated(sessionId);
  
  // IMPORTANT: Check for in-flight request FIRST, before clearing cache
  // This ensures deduplication works even when multiple components call simultaneously
  if (inFlightRequests[url]) {
    return inFlightRequests[url];
  }
  
  // If invalidated or force refresh, clear the cached result (but not in-flight)
  if (wasInvalidated || forceRefresh) {
    delete svgCache[url];
  }
  
  // Return cached SVG if available
  if (svgCache[url]) {
    return svgCache[url];
  }

  // Create new request and store promise
  inFlightRequests[url] = (async () => {
    try {
      // Add cache-busting if SVG was invalidated (use shared timestamp for deduplication)
      let fetchUrl = url;
      if (wasInvalidated) {
        const separator = url.includes('?') ? '&' : '?';
        fetchUrl = `${url}${separator}_t=${getSvgInvalidationTimestamp()}`;
      }
      
      const response = await fetch(fetchUrl, {
        ...options,
        headers: {
          ...options.headers,
          // Always add no-cache headers for session SVGs to prevent stale data after corner reassignment
          // Browser cache can serve old SVGs even after corners are updated
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch SVG: ${response.status}`);
      }

      const svg = await response.text();
      
      // Cache the result (using original URL as key, not cache-busted URL)
      svgCache[url] = svg;
      
      // Clear invalidation flag after successful fetch
      if (wasInvalidated) {
        clearSvgInvalidation();
      }
      
      return svg;
    } catch (error) {
      console.error(`Error fetching SVG ${url}:`, error);
      throw error;
    } finally {
      // Clear in-flight request after completion
      delete inFlightRequests[url];
    }
  })();

  return inFlightRequests[url];
};

export const peekSvgCache = (url) => (url ? svgCache[url] || null : null);

/**
 * Clear SVG cache (useful when data is updated)
 * @param {string} url - Optional URL to clear specific cache entry
 */
export const clearSvgCache = (url = null) => {
  if (url) {
    delete svgCache[url];
  } else {
    Object.keys(svgCache).forEach(key => delete svgCache[key]);
  }
};

/**
 * Get cache key for session track SVG
 */
export const getTrackSvgCacheKey = (sessionId) => {
  return `track-svg-${sessionId}`;
};

/**
 * Get cache key for corner SVG
 */
export const getCornerSvgCacheKey = (sessionId, cornerNumber) => {
  return `corner-svg-${sessionId}-${cornerNumber}`;
};
